import React, { useState } from 'react';
import { FileSpreadsheet, Download, FileText } from 'lucide-react';
import { Button, Card, FileUploadZone, ProgressBar, Alert, Badge, NoteCard } from '../shared';
import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist';
import { saveAs } from 'file-saver';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import '../../styles/design-system.css';

// Set up PDF.js worker
GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

const PDFtoEXCEL = () => {
    const [file, setFile] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [progress, setProgress] = useState(0);
    const [pageCount, setPageCount] = useState(0);

    const handleFileSelected = async (files) => {
        if (files && files.length > 0) {
            const selectedFile = files[0];
            setFile(selectedFile);

            try {
                const arrayBuffer = await selectedFile.arrayBuffer();
                const loadingTask = getDocument({ data: arrayBuffer });
                const pdf = await loadingTask.promise;
                setPageCount(pdf.numPages);
                toast.success(`PDF loaded: ${pdf.numPages} pages`);
            } catch (error) {
                toast.error(`Failed to load PDF: ${error.message}`);
            }
        }
    };

    const detectTables = (text) => {
        // Simple table detection: look for lines with multiple spaces/tabs
        const lines = text.split('\n');
        const tableData = [];

        for (const line of lines) {
            // Split by multiple spaces or tabs
            const cells = line.split(/\s{2,}|\t+/).filter(cell => cell.trim());

            if (cells.length > 1) {
                tableData.push(cells);
            }
        }

        return tableData;
    };

    const convertToEXCEL = async () => {
        if (!file) return;

        try {
            setIsProcessing(true);
            setProgress(10);

            const arrayBuffer = await file.arrayBuffer();
            const loadingTask = getDocument({ data: arrayBuffer });
            const pdf = await loadingTask.promise;

            setProgress(30);

            const workbook = XLSX.utils.book_new();

            // Process each page
            for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
                const page = await pdf.getPage(pageNum);
                const textContent = await page.getTextContent();

                const pageText = textContent.items
                    .map(item => item.str)
                    .join(' ');

                // Detect tables in the text
                const tableData = detectTables(pageText);

                if (tableData.length > 0) {
                    // Create worksheet from table data
                    const worksheet = XLSX.utils.aoa_to_sheet(tableData);
                    XLSX.utils.book_append_sheet(workbook, worksheet, `Page ${pageNum}`);
                } else {
                    // If no table detected, create a simple text sheet
                    const textData = pageText.split('\n').map(line => [line.trim()]).filter(row => row[0]);
                    const worksheet = XLSX.utils.aoa_to_sheet(textData);
                    XLSX.utils.book_append_sheet(workbook, worksheet, `Page ${pageNum}`);
                }

                setProgress(30 + ((pageNum / pdf.numPages) * 60));
            }

            setProgress(95);

            // Generate XLSX file
            const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
            const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

            setProgress(100);

            const fileName = file.name.replace(/\.pdf$/i, '');
            saveAs(blob, `${fileName}.xlsx`);

            toast.success('PDF converted to Excel successfully!');
            toast.info(`Created ${workbook.SheetNames.length} sheet(s) from ${pdf.numPages} page(s)`);

            setTimeout(() => {
                setProgress(0);
            }, 1000);

        } catch (error) {
            console.error('Conversion error:', error);
            toast.error(`Failed to convert: ${error.message}`);
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <div>
            {/* Header */}
            <div className="tool-header">
                <h1 className="tool-title">
                    <FileSpreadsheet size={36} style={{ display: 'inline', marginRight: 'var(--spacing-3)', color: 'var(--color-accent)' }} />
                    PDF to EXCEL
                </h1>
                <p className="tool-description">
                    Extract tables and data from PDF documents and convert to Excel format (XLSX).
                </p>
            </div>

            {/* File Upload */}
            {!file ? (
                <FileUploadZone
                    onFilesSelected={handleFileSelected}
                    acceptedTypes={['application/pdf']}
                    multiple={false}
                    title="Drop PDF file here"
                    description="or click to browse (works best with PDFs containing tables)"
                />
            ) : (
                <Alert variant="success" title="PDF Loaded">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
                            <FileText size={16} />
                            <span>{file.name}</span>
                            {pageCount > 0 && <Badge variant="info">{pageCount} pages</Badge>}
                        </div>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                                setFile(null);
                                setPageCount(0);
                            }}
                        >
                            Change PDF
                        </Button>
                    </div>
                </Alert>
            )}

            {file && (
                <>
                    {/* Info */}
                    <div style={{ marginTop: 'var(--spacing-6)' }}>
                        <Card>
                            <h3 style={{
                                fontSize: 'var(--text-lg)',
                                fontWeight: 'var(--font-bold)',
                                marginBottom: 'var(--spacing-3)'
                            }}>
                                Table Detection
                            </h3>
                            <ul style={{
                                fontSize: 'var(--text-sm)',
                                color: 'var(--color-text-secondary)',
                                lineHeight: 1.6,
                                paddingLeft: 'var(--spacing-5)'
                            }}>
                                <li>Automatically detects tables in PDF pages</li>
                                <li>Each page becomes a separate Excel sheet</li>
                                <li>Preserves table structure where possible</li>
                                <li>Works best with well-formatted PDF tables</li>
                            </ul>
                        </Card>
                    </div>

                    {/* Progress Bar */}
                    {isProcessing && (
                        <div style={{ marginTop: 'var(--spacing-6)' }}>
                            <ProgressBar value={progress} striped showLabel />
                        </div>
                    )}

                    {/* Convert Button */}
                    <div style={{ marginTop: 'var(--spacing-6)' }}>
                        <Button
                            variant="primary"
                            size="lg"
                            onClick={convertToEXCEL}
                            disabled={isProcessing}
                            loading={isProcessing}
                            fullWidth
                        >
                            <Download size={20} />
                            Convert to EXCEL
                        </Button>
                    </div>

                    {/* Warning */}
                    <NoteCard variant="warning">
                        <strong>Note:</strong> Table detection is based on text spacing. Complex tables, merged cells, or non-standard layouts may not convert perfectly. For best results, use PDFs with clearly defined tables.
                    </NoteCard>
                </>
            )}
        </div>
    );
};

export default PDFtoEXCEL;
